import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../setupAxios';
import { apiUrl, apiUrlWithAccessToken } from '../config/api';
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;

function buildPageNumbers(currentPage, totalPages) {
  if (totalPages <= 1) {
    return [1];
  }
  const pages = [];
  for (let i = 1; i <= totalPages; i += 1) {
    pages.push(i);
  }
  return pages;
}

function GalleryDeviceList({ categoryKey, categoryLabel, onSelectDevice, formatCreateTime }) {
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setSort('name');
    setPage(1);
    setPageSize(20);
    setKeywordInput('');
    setKeyword('');
  }, [categoryKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setKeyword(keywordInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [keywordInput]);

  useEffect(() => {
    setPage(1);
  }, [keyword, pageSize, sort, categoryKey]);

  useEffect(() => {
    let cancelled = false;

    const fetchDevices = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {
          sort,
          page,
          size: pageSize,
        };
        if (keyword) {
          params.q = keyword;
        }

        const response = await axios.get(
          apiUrl(`/api/categories/${encodeURIComponent(categoryKey)}/devices`),
          { params }
        );
        if (!cancelled) {
          setPageData(response.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 401) {
            setError('登录已过期，请重新登录');
          } else {
            setError('加载设备列表失败，请确认后端服务已启动且分类目录存在');
          }
          setPageData(null);
          console.error('Device list error:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDevices();
    return () => {
      cancelled = true;
    };
  }, [categoryKey, sort, page, pageSize, keyword]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, sort, pageSize, keyword, categoryKey]);

  const devices = pageData?.items || [];
  const totalElements = pageData?.totalElements ?? 0;
  const totalAll = pageData?.totalAll ?? totalElements;
  const totalPages = pageData?.totalPages ?? 1;
  const currentPage = pageData?.page ?? page;
  const isFiltering = Boolean(keyword);

  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handleSortChange = (nextSort) => {
    if (nextSort === sort) return;
    setSort(nextSort);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
  };

  const handleClearSearch = () => {
    setKeywordInput('');
  };

  if (loading && !pageData) {
    return <div className="loading">加载设备列表中...</div>;
  }

  if (error) {
    return <div className="gallery-error">{error}</div>;
  }

  if (totalAll === 0) {
    return (
      <p className="gallery-empty">
        {categoryLabel} 分类下暂无设备目录（请检查 img/{categoryKey}/ 下是否有设备文件夹）
      </p>
    );
  }

  return (
    <div className="device-list-card">
      <div className="device-list-filters device-list-filters--inline">
        <label className="device-search-field">
          <span className="device-search-label">搜索筛选：</span>
          <input
            type="search"
            className="device-search-input"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            placeholder="输入设备名称或 ICCID"
            maxLength={25}
          />
        </label>
        {keywordInput && (
          <button type="button" className="device-sort-btn device-sort-btn--compact" onClick={handleClearSearch}>
            清除
          </button>
        )}
        <label className="device-page-size-field">
          <span className="device-search-label">每页条数</span>
          <select
            className="device-page-size-select"
            value={pageSize}
            onChange={handlePageSizeChange}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} 条/页
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="device-list-toolbar">
        <span className="device-list-hint2">排序方式：</span>
        {sort === 'time' ? (
          <>
            <strong className="device-sort-active">按最近更新</strong>
            <span className="device-list-hint3">排序方式选择：</span>
            <button type="button" className="device-sort-btn" onClick={() => handleSortChange('name')} title="按设备名">按设备名</button>
          </>
        ) : (
          <>
            <strong className="device-sort-active">按设备名</strong>
            <span className="device-list-hint3">排序方式选择：</span>
            <button type="button" className="device-sort-btn" onClick={() => handleSortChange('time')} title="按最近更新">按最近更新</button>
          </>
        )}
        <span className="device-list-hint3">共 {totalElements} 台设备 {isFiltering && totalElements !== totalAll ? `（全部 ${totalAll} 台）` : ''}</span>
      </div>
      {totalElements === 0 ? (
        <p className="gallery-empty">未找到匹配「{keyword}」的设备，请尝试其他关键字。</p>
      ) : (
        <>
          <div className="device-list-table-wrap">
            <table className="device-list-table">
              <thead>
                <tr>
                  <th>设备目录</th>
                  <th>图片数量</th>
                  <th>ICCID Number</th>
                  <th>日志</th>
                  <th>最近更新时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((dev) => (
                  <tr key={dev.deviceId}>
                    <td>
                      <code className="device-list-code">{dev.deviceId}</code>
                    </td>
                    <td>{dev.imageCount ?? 0}</td>
                    <td>
                      {dev.iccid && dev.idHtmlUrl ? (
                      <a href={apiUrlWithAccessToken(dev.idHtmlUrl)} target="_blank" rel="noopener noreferrer" className="device-link" title="查看iccid信息">{dev.iccid}</a>
                      ) : (
                        <span className="device-list-muted">-</span>
                      )}
                    </td>
                    <td>
                      {dev.hasDeviceLog ? (
                      <Link to={`/gallery/${encodeURIComponent(categoryKey)}/log/${encodeURIComponent(dev.deviceId)}`} className="device-link" title="查看Device.log">查看日志</Link>
                      ) : (
                        <span className="device-list-muted">-</span>
                      )}
                    </td>
                    <td>{formatCreateTime(dev.lastModified)}</td>
                    <td>
                        <button type="button" className="device-link device-link-btn" onClick={() => onSelectDevice(dev.deviceId)} title="查看图片">查看图片</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="device-list-pager">
              <span className="device-pager-summary"> 第 {currentPage} / {totalPages} 页</span>
              {currentPage > 1 ? (
                <>
                  <button type="button" className="device-pager-btn" onClick={() => setPage(1)} title="首页">首页</button>
                  <button type="button" className="device-pager-btn" onClick={() => setPage(currentPage - 1)} title="上一页">上一页</button>
                </>
              ) : (
                <>
                  <span className="device-pager-disabled">首页</span>
                  <span className="device-pager-disabled">上一页</span>
                </>
              )}
              {pageNumbers.map((pageNumber) =>
                pageNumber === currentPage ? (
                  <strong key={pageNumber} className="device-pager-current">{pageNumber}</strong>
                ) : (
                  <button key={pageNumber} type="button" className="device-pager-btn" onClick={() => setPage(pageNumber)} title={pageNumber}>{pageNumber}</button>
                )
              )}
              {currentPage < totalPages ? (
                <>
                  <button type="button" className="device-pager-btn" onClick={() => setPage(currentPage + 1)} title="下一页">下一页</button>
                  <button type="button" className="device-pager-btn" onClick={() => setPage(totalPages)} title="末页">末页</button>
                </>
              ) : (
                <>
                  <span className="device-pager-disabled">下一页</span>
                  <span className="device-pager-disabled">末页</span>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GalleryDeviceList;
